<template>
  <Modal closable overflow>
    <template v-slot:header>
      <h2>
        <div class="place-index">{{ getPlaceSymbol(routeId, placeIndex) }}</div>
        {{ placeEdited.name }}
      </h2>
    </template>

    <div id="place-settings">
      <div class="setting">
        <h4>{{ $t('place.name') }}</h4>
        <input v-model="placeEdited.name" :placeholder="$t('place.name_placeholder')" />
      </div>

      <div class="setting">
        <h4>{{ $t('place.address') }}</h4>
        <div class="address">{{ placeEdited.address }}</div>
        <AppIcon class="edit-address" :class="{'active': showLocationSearch}" name="create" @click="showLocationSearch = !showLocationSearch" />
      </div>
      <LocationSearch v-if="showLocationSearch" @selected="changeLocation" style="margin-top: .5rem;" />

      <template v-if="!(placeEdited.first || placeEdited.last)">
        <div class="divider"></div>
        <div class="setting"  v-if="!(placeEdited.first || placeEdited.last)">
          <h4>{{ $t("place.diff_break_time") }}</h4>
          <div><InputCheck v-model="customBreak" isSwitch /></div>
        </div>
        
        <div class="setting" v-if="customBreak && !(placeEdited.first || placeEdited.last)">
          <h4>{{ $t('route.break_time') }}</h4>
          <div><input v-model="placeEdited.breakTime" style="width:5rem" type="number" min="0" /> min</div>
        </div>
      </template>
    </div>

    <template v-slot:footer>
      <AppButton :loading="saving" @click="update" color="primary">{{ $t("buttons.save_and_close") }}</AppButton>
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent, PropType, reactive, Ref, ref, watch } from 'vue'
import { cleanObject, compareObjects, debounceRef } from '@/api/tools'
import InputCheck from '@/components/inputs/InputCheck.vue'
import LocationSearch from '@/components/route/LocationSearch.vue'
import { store } from '@/store'
import { Location } from '../../api/models/Location'
import { Place } from '@/api/tripply/models'
import { getPlaceSymbol } from '@/api/tripply'

export default defineComponent({
  name: 'PlaceSettings',
  props: {
    open: Boolean,
    routeId: {
      type: String,
      required: true
    },
    placeIndex: {
      type: Number,
      required: true
    },
    place: {
      type: Object as PropType<Place>,
      required: true
    }
  },
  components: { InputCheck, LocationSearch },
  setup(props, { emit }) {
    const saving = ref(false)
    const showLocationSearch = ref(false)
    const addressChanged = ref(false)
    
    function watchChanges<T extends object>(source: T, target: T) {
      source = cleanObject(source)
      const changes: Ref<Partial<T>> = ref({})
      watch(target, (newTarget) => {
        changes.value = compareObjects<T>(source, cleanObject(newTarget))
      })
      return changes
    }

    const placeEdited = reactive<Place>({ ...props.place })

    const customBreak = ref(placeEdited.breakTime >= 0)
    
    if (placeEdited.breakTime >= 0) placeEdited.breakTime /= 60

    watch(customBreak, (val) => {
      placeEdited.breakTime = val ? 0 : -1
    })

    async function changeLocation(location: Location) {
      // Update name if address is changed
      if (placeEdited.name == placeEdited.address.split(',')[0]) {
        placeEdited.name = location.name
      }
      placeEdited.address = location.address
      placeEdited.coords = location.coords
      addressChanged.value = true
      showLocationSearch.value = false
    }

    async function update() {
      saving.value = true
      const updatedPlace = {...placeEdited}
      if (updatedPlace.breakTime >= 0) updatedPlace.breakTime *= 60   
      await store.route.updatePlace(props.routeId, props.placeIndex, updatedPlace)
      saving.value = false
      store.modal.hideModal('PlaceSettings')
    }

    return { placeEdited, customBreak, saving, update, changeLocation, showLocationSearch, getPlaceSymbol }
  }
})
</script>

<style lang="scss" scoped>
.setting {
  margin: 1rem 0;
  justify-content: space-between;
  align-items: center;
  display: flex;
  h4 {
    margin: 0;
  }

  .edit-address {
    cursor: pointer;
    &.active {
      color: var(--color-primary);
    }
  }
  .address {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-left: 1rem;
    width: 100%;
    text-align: right;
  }
}

.place-index {
  display: inline-flex;
  background: var(--color-primary);
  border-radius: 100%;
  height: 2rem;
  width: 2rem;
  justify-content: center;
  align-items: center;
  color: var(--color-on-primary);
  flex: 2rem 0 0;
  margin-right: .5rem;
  margin-top: -.15rem;
  font-size: 1.25rem;
}

.modal-header h2 {
  text-align: left !important;
}


</style>